import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import infoSvg from 'assets/icons/info.svg';
import Button, { Color, TextOverflow } from 'lib/Button';
import Modal from 'lib/Modal';
import {
  getAdvertiser,
  getAgency,
  getBrand,
  getProductCategory,
  getSpecialist,
} from 'store/dealManagement/selectors/getCommonDealDetails';
import {
  getBackupAdvertiser,
  getBackupAgency,
  getBackupBrand,
  getBackupProductCategory,
  getBackupSpecialist,
} from 'store/dealManagement/selectors/getBackupCommonDealDetails';
import { getIsAdvertiserInfoChanged } from 'store/dealManagement/selectors';
import { AdvertiserInfoConfirmationModalProps } from './AdvertiserInfoConfirmationModal.types';

const AdvertiserInfoConfirmationModal: React.FC<AdvertiserInfoConfirmationModalProps> = ({
  isVisible,
  onConfirm,
  onUndoChanges,
}) => {
  const advertiser = useSelector(getAdvertiser);
  const brand = useSelector(getBrand);
  const productCategory = useSelector(getProductCategory);
  const agency = useSelector(getAgency);
  const specialist = useSelector(getSpecialist);
  const backupAdvertiser = useSelector(getBackupAdvertiser);
  const backupBrand = useSelector(getBackupBrand);
  const backupProductCategory = useSelector(getBackupProductCategory);
  const backupAgency = useSelector(getBackupAgency);
  const backupSpecialist = useSelector(getBackupSpecialist);
  const { isAdvertiserChanged, isBrandChanged, isProductCategoryChanged, isAgencyChanged, isSpecialistChanged } =
    useSelector(getIsAdvertiserInfoChanged);

  const modalContent = useMemo(() => {
    if (!isVisible) return '';

    const advertiserChange =
      isAdvertiserChanged && `The Advertiser from ${backupAdvertiser?.name} to ${advertiser?.name}`;

    const brandChange = isBrandChanged && `The Brand from ${backupBrand?.name} to ${brand?.name}`;

    const productCategoryChange =
      isProductCategoryChanged &&
      `The Product category from ${backupProductCategory?.name} to ${productCategory?.name}`;

    const agencyChange =
      isAgencyChanged && `The Agency from ${backupAgency?.name || '"none"'} to ${agency?.name || '"none"'}`;

    const specialistChange =
      isSpecialistChanged &&
      `The Specialist from ${backupSpecialist?.name || '"none"'} to ${specialist?.name || '"none"'}`;

    const changes = [advertiserChange, brandChange, productCategoryChange, agencyChange, specialistChange].filter(
      Boolean,
    );

    return (
      <>
        You are changing:
        <ul className="list-disc list-inside mt-1 mb-2">
          {changes.map((change, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>{change}</li>
          ))}
        </ul>
        This may impact your availability. Please save to proceed?
      </>
    );
  }, [isVisible]);

  return (
    <Modal
      dataTestId="advertiser-info-confirmation-modal"
      isOpen={isVisible}
      onClose={onUndoChanges}
      icon={infoSvg}
      title="Save changes"
      actionButtons={
        <>
          <Button
            onClick={onUndoChanges}
            label="Undo changes"
            color={Color.DANGER}
            textOverflow={TextOverflow.VISIBLE}
          />
          <Button onClick={onConfirm} label="Save" color={Color.PRIMARY} textOverflow={TextOverflow.VISIBLE} />
        </>
      }
    >
      <div className="body-base text-neutral-950-opacity-60">{modalContent}</div>
    </Modal>
  );
};

export default AdvertiserInfoConfirmationModal;
